import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Users, Mail, Phone, Building, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function KlantenPage() {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    naam: '',
    email: '',
    adres: '',
    postcode: '',
    plaats: '',
    telefoon: '',
    btw_nummer: '',
    kvk_nummer: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (client = null) => {
    if (client) {
      setEditing(client);
      setForm({
        naam: client.naam,
        email: client.email || '',
        adres: client.adres || '',
        postcode: client.postcode || '',
        plaats: client.plaats || '',
        telefoon: client.telefoon || '',
        btw_nummer: client.btw_nummer || '',
        kvk_nummer: client.kvk_nummer || ''
      });
    } else {
      setEditing(null);
      setForm({
        naam: '',
        email: '',
        adres: '',
        postcode: '',
        plaats: '',
        telefoon: '',
        btw_nummer: '',
        kvk_nummer: ''
      });
    }
    setShowDialog(true);
  };

  const saveClient = async () => {
    if (!form.naam.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    try {
      if (editing) {
        await api.put(`/clients/${editing.client_id}`, form);
        toast.success(t('clients.clientSaved'));
      } else {
        await api.post('/clients', form);
        toast.success(t('clients.clientSaved'));
      }
      setShowDialog(false);
      fetchClients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : 
                       t('errors.somethingWentWrong');
      toast.error(errorMsg);
    }
  };

  const deleteClient = async (client) => {
    if (!confirm(t('clients.confirmDelete'))) return;

    try {
      await api.delete(`/clients/${client.client_id}`);
      toast.success(t('clients.clientDeleted'));
      fetchClients();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const generatePortalLink = async (client) => {
    try {
      const res = await api.post(`/clients/${client.client_id}/portal-token`);
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/portal/${res.data.token}`;
      setPortalUrl(url);
      setShowPortalDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const copyPortalLink = () => {
    navigator.clipboard.writeText(portalUrl);
    toast.success(t('clients.linkCopied'));
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-12 bg-muted rounded-xl w-1/3" />
      <div className="h-64 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="klanten-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground">{t('clients.title')}</h1>
          <p className="text-muted-foreground">{t('clients.subtitle')}</p>
        </div>
        <Button 
          className="bg-cyan-500 hover:bg-cyan-600 btn-active"
          onClick={() => openDialog()}
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('clients.addClient')}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {clients.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clients.name')}</TableHead>
                    <TableHead>{t('clients.email')}</TableHead>
                    <TableHead>{t('clients.phone')}</TableHead>
                    <TableHead>{t('clients.city')}</TableHead>
                    <TableHead>{t('clients.cocNumber')}</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.client_id}>
                      <TableCell className="font-medium">{client.naam}</TableCell>
                      <TableCell>
                        {client.email ? (
                          <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {client.email}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {client.telefoon ? (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            {client.telefoon}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{client.plaats || '-'}</TableCell>
                      <TableCell>{client.kvk_nummer || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => generatePortalLink(client)}
                            title={t('clients.generatePortalLink')}
                            data-testid={`portal-link-${client.client_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openDialog(client)}
                            data-testid={`edit-client-${client.client_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => deleteClient(client)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-client-${client.client_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">{t('clients.noClients')}</p>
              <Button onClick={() => openDialog()} className="bg-cyan-500 hover:bg-cyan-600">
                <Plus className="w-4 h-4 mr-2" />
                {t('clients.addClient')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? t('clients.editClient') : t('clients.addClient')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>{t('clients.name')} *</Label>
              <Input
                value={form.naam}
                onChange={(e) => setForm(prev => ({ ...prev, naam: e.target.value }))}
                placeholder={t('clients.name')}
                data-testid="client-naam-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="info@company.com"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('clients.phone')}</Label>
                <Input
                  value={form.telefoon}
                  onChange={(e) => setForm(prev => ({ ...prev, telefoon: e.target.value }))}
                  placeholder="06-12345678"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('clients.address')}</Label>
              <Input
                value={form.adres}
                onChange={(e) => setForm(prev => ({ ...prev, adres: e.target.value }))}
                placeholder={t('clients.address')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.postalCode')}</Label>
                <Input
                  value={form.postcode}
                  onChange={(e) => setForm(prev => ({ ...prev, postcode: e.target.value }))}
                  placeholder="1234 AB"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('clients.city')}</Label>
                <Input
                  value={form.plaats}
                  onChange={(e) => setForm(prev => ({ ...prev, plaats: e.target.value }))}
                  placeholder="Amsterdam"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.vatNumber')}</Label>
                <Input
                  value={form.btw_nummer}
                  onChange={(e) => setForm(prev => ({ ...prev, btw_nummer: e.target.value }))}
                  placeholder="NL123456789B01"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('clients.cocNumber')}</Label>
                <Input
                  value={form.kvk_nummer}
                  onChange={(e) => setForm(prev => ({ ...prev, kvk_nummer: e.target.value }))}
                  placeholder="12345678"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={saveClient} data-testid="client-save-btn">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal Link Dialog */}
      <Dialog open={showPortalDialog} onOpenChange={setShowPortalDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('clients.generatePortalLink')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {t('clients.portalLinkGenerated')}
            </p>
            <div className="flex gap-2">
              <Input 
                value={portalUrl} 
                readOnly 
                className="font-mono text-sm"
                data-testid="portal-url-input"
              />
              <Button onClick={copyPortalLink} variant="outline" data-testid="copy-portal-link-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              onClick={() => window.open(portalUrl, '_blank')} 
              className="w-full"
              variant="outline"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Openen in nieuw tabblad
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPortalDialog(false)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
